<template>
  <v-container fluid class="pl-4 pr-4 pb-4 pt-1">
    <!-- Compact header (matches Telegram style) -->
    <v-row align="center" class="mb-0 px-0" style="min-height: 48px;">
      <v-col cols="auto" class="d-flex align-center ga-2">
        <v-btn
          color="primary"
          variant="outlined"
          prepend-icon="mdi-plus"
          @click="createProject"
        >
          Создать проект
        </v-btn>
      </v-col>
      <v-col class="text-center">
        <h4 class="mb-0 d-inline-flex align-center ga-1">
          Проекты
          <HelpHint section="projects" anchor="list" text="Запуск, остановка, пересканирование и колонки списка" />
        </h4>
      </v-col>
      <v-col cols="auto">
        <div class="d-flex align-center ga-2">
          <span class="text-caption">На странице:</span>
          <v-select
            v-model="limit"
            :items="PER_PAGE"
            variant="outlined"
            density="compact"
            hide-details
            style="max-width: 160px"
            @update:model-value="onLimitChange"
          ></v-select>
          <v-pagination
            v-model="currentPage"
            :length="totalPages"
            :total-visible="5"
            size="small"
            @update:model-value="onPageChange"
          ></v-pagination>
          <span class="text-caption font-weight-bold">Найдено: {{ total }}</span>
          <v-btn
            :icon="panelVisible ? 'mdi-filter-off' : 'mdi-filter'"
            variant="outlined"
            @click="panelVisible = !panelVisible"
            :color="panelVisible ? 'primary' : 'default'"
            class="ml-2"
          ></v-btn>
        </div>
      </v-col>
    </v-row>

    <!-- No projects yet -->
    <no-projects-alert v-if="noProjectsYet"></no-projects-alert>

    <v-row>
      <!-- Main table column -->
      <v-col :cols="panelVisible ? 10 : 12" class="table-column-transition">
        <v-card>
          <v-card-text class="pa-0">
            <v-data-table
              :headers="headers"
              :items="projects"
              :items-per-page="limit"
              :loading="isLoading"
              :sort-by="sortBy"
              @update:sort-by="updateSort"
              class="elevation-1"
              hover
              fixed-header
              height="calc(100vh - 236px)"
              @click:row="handleRowClick"
            >
              <!-- Name column -->
              <template v-slot:[`item.name`]="{ item }">
                <div>
                  <span class="font-weight-bold">{{ item.name }}</span>
                  <!-- in a team: who made it and who changed it last (task 59) -->
                  <div v-if="item.team_id" class="text-caption text-medium-emphasis">
                    создал {{ item.created_by_name || '—' }}<span v-if="item.updated_by_name"> · изменил {{ item.updated_by_name }}</span>
                  </div>
                  <div v-if="item.tags && item.tags.length > 0" class="mt-1">
                    <v-chip
                      v-for="tag in item.tags"
                      :key="tag.id"
                      :color="tag.color"
                      size="x-small"
                      label
                      class="mr-1"
                    >
                      {{ tag.name }}
                    </v-chip>
                  </div>
                </div>
              </template>

              <!-- Formatted numbers -->
              <template v-slot:[`item.total_links`]="{ item }">
                <div class="text-right">
                  <!-- the first check took only the newest messages (admin settings «Лимит сообщений первой проверки») -->
                  <v-tooltip v-if="firstCheckCut(item)" location="top" max-width="360">
                    <template #activator="{ props }">
                      <v-icon v-bind="props" size="small" color="warning" class="mr-1">mdi-information-outline</v-icon>
                    </template>
                    {{ firstCheckText(item.first_check) }}
                  </v-tooltip>
                  {{ formatNumber(item.total_links) }}
                </div>
              </template>

              <template v-slot:[`item.new_links`]="{ item }">
                <div class="text-right">{{ formatNumber(item.new_links) }}</div>
              </template>

              <!-- Status column -->
              <template v-slot:[`item.status`]="{ item }">
                <v-chip
                  :color="getStatusColor(item.status)"
                  size="x-small"
                  label
                >
                  {{ getStatusLabel(item.status) }}
                </v-chip>
              </template>

              <!-- Actions column -->
              <template v-slot:[`item.actions`]="{ item }">
                <v-tooltip text="Редактировать">
                  <template v-slot:activator="{ props }">
                    <v-btn
                      v-bind="props"
                      icon
                      size="small"
                      variant="text"
                      :disabled="isInProgress(item.status)"
                      @click.stop="editProject(item)"
                    >
                      <v-icon>mdi-pencil</v-icon>
                    </v-btn>
                  </template>
                </v-tooltip>
                <v-tooltip text="Удалить">
                  <template v-slot:activator="{ props }">
                    <v-btn
                      v-bind="props"
                      icon
                      size="small"
                      variant="text"
                      color="error"
                      :disabled="isInProgress(item.status)"
                      @click.stop="deleteProject(item)"
                    >
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </template>
                </v-tooltip>
                <v-tooltip text="Пересканировать по имеющимся данным (удалит текущие сообщения проекта)">
                  <template v-slot:activator="{ props }">
                    <v-btn
                      v-bind="props"
                      icon
                      size="small"
                      variant="text"
                      color="grey"
                      :disabled="isInProgress(item.status)"
                      @click.stop="clearFindings(item)"
                    >
                      <v-icon>mdi-refresh</v-icon>
                    </v-btn>
                  </template>
                </v-tooltip>
                <v-tooltip v-if="item.status !== 'active'" text="Запустить">
                  <template v-slot:activator="{ props }">
                    <v-btn
                      v-bind="props"
                      icon
                      size="small"
                      variant="text"
                      color="success"
                      @click.stop="startProject(item)"
                    >
                      <v-icon>mdi-play</v-icon>
                    </v-btn>
                  </template>
                </v-tooltip>
                <v-tooltip v-else text="Остановить">
                  <template v-slot:activator="{ props }">
                    <v-btn
                      v-bind="props"
                      icon
                      size="small"
                      variant="text"
                      color="warning"
                      @click.stop="stopProject(item)"
                    >
                      <v-icon>mdi-stop</v-icon>
                    </v-btn>
                  </template>
                </v-tooltip>
              </template>

              <!-- Bottom pagination -->
              <template v-slot:bottom>
                <!-- Managed by header pagination -->
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Filters column with transition -->
      <v-slide-x-reverse-transition>
        <v-col v-if="panelVisible" cols="2" key="filters">
          <v-card>
            <v-card-title class="text-subtitle-1">Фильтры</v-card-title>
            <v-card-text class="pa-3">
              <!-- Search by name/description -->
              <v-text-field
                v-model="filters.search"
                label="Название / описание"
                variant="outlined"
                density="compact"
                hide-details
                class="mb-4"
                clearable
                @blur="onFilterChange"
                @keydown.enter="onFilterChange"
                @click:clear="filters.search = ''; onFilterChange()"
              ></v-text-field>

              <!-- Tags -->
              <div class="mb-4">
                <div class="text-subtitle-2 mb-2">Теги</div>
                <v-autocomplete
                  v-model="filters.selectedTags"
                  :items="availableTags"
                  item-title="name"
                  item-value="id"
                  multiple
                  chips
                  closable-chips
                  variant="outlined"
                  density="compact"
                  hide-details
                  clearable
                  placeholder="Выберите теги"
                  @update:model-value="onFilterChange"
                >
                  <template #chip="{ props, item }">
                    <v-chip
                      v-bind="props"
                      :text="item.raw.name"
                      :color="item.raw.color"
                      closable
                      size="small"
                    />
                  </template>
                </v-autocomplete>
              </div>

              <!-- Status -->
              <div class="mb-4">
                <div class="text-subtitle-2 mb-2">Статус</div>
                <v-select
                  v-model="filters.status"
                  :items="statusOptions"
                  item-title="text"
                  item-value="value"
                  variant="outlined"
                  density="compact"
                  hide-details
                  clearable
                  @update:model-value="onFilterChange"
                ></v-select>
              </div>

              <!-- Filter actions -->
              <div class="d-flex justify-space-between">
                <v-btn
                  color="primary"
                  variant="outlined"
                  @click="resetFilters"
                  prepend-icon="mdi-filter-remove"
                >
                  Сбросить
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-slide-x-reverse-transition>
    </v-row>

    <!-- Delete Confirmation Dialog -->
    <v-dialog
      v-model="showDeleteDialog"
      max-width="500"
      :persistent="isDeleting"
    >
      <v-card>
        <v-card-title>Подтверждение удаления</v-card-title>
        <v-card-text>
          Вы уверены, что хотите удалить проект "{{ projectToDelete?.name }}"?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            variant="text"
            :disabled="isDeleting"
            @click="showDeleteDialog = false"
          >
            Отмена
          </v-btn>
          <v-btn
            color="error"
            variant="text"
            :loading="isDeleting"
            :disabled="isDeleting"
            @click="confirmDelete"
          >
            Удалить
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Clear Findings Confirmation Dialog -->
    <v-dialog
      v-model="showClearFindingsDialog"
      max-width="500"
    >
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon color="warning" class="mr-2">mdi-alert</v-icon>
          Пересканировать проект
        </v-card-title>
        <v-card-text>
          <p>Пересканировать проект "{{ projectToClear?.name }}" по уже собранным данным?</p>
          <p class="text-caption text-grey mt-2">
            Будут удалены все {{ projectToClear?.total_links || 0 }} найденных сообщений по этому проекту,
            и поиск заново переберёт уже собранные данные по текущему запросу (удобно после его изменения).
            Источники повторно не скачиваются. Пересканирование запустится при следующем прогоне мониторинга.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            variant="text"
            @click="showClearFindingsDialog = false"
          >
            Отмена
          </v-btn>
          <v-btn
            color="warning"
            variant="flat"
            @click="confirmClearFindings"
            :loading="clearingFindings"
          >
            Пересканировать
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import NoProjectsAlert from "@/components/projects/NoProjectsAlert.vue";
import { mapState } from 'vuex';
import apiClient from "@/apiClient";

const PER_PAGE = [5, 10, 20, 50];

function readPerPage() {
  try {
    const raw = document.cookie.split(';').map(c => c.trim()).find(c => c.startsWith('projects_per_page='));
    const n = raw ? parseInt(raw.slice('projects_per_page='.length), 10) : NaN;
    return PER_PAGE.includes(n) ? n : 10;
  } catch (e) { return 10; }
}

export default {
  emits: ["show-message"],
  components: { NoProjectsAlert },
  data() {
    return {
      projects: [],
      total: 0,
      currentPage: 1,
      PER_PAGE,
      // remembered in the cookie projects_per_page, like the width of the menu (nav_drawer_width)
      limit: readPerPage(),
      isLoading: false,
      panelVisible: false,
      sortBy: [{ key: 'created_at', order: 'desc' }],
      headers: [
        { title: 'Проект', key: 'name', sortable: true, width: '30%' },
        { title: 'Всего', key: 'total_links', sortable: true, width: '8%', align: 'end' },
        { title: 'Новых', key: 'new_links', sortable: true, width: '8%', align: 'end' },
        { title: 'Создан', key: 'created_at', sortable: true, width: '14%' },
        { title: 'Выполнен', key: 'executed_at', sortable: true, width: '14%' },
        { title: 'Статус', key: 'status', sortable: true, width: '10%' },
        { title: 'Действия', key: 'actions', sortable: false, width: '16%' },
      ],
      // Filters
      loaded: false,
      filters: {
        search: '',
        selectedTags: [],
        status: null,
      },
      availableTags: [],
      statusOptions: [
        { text: 'Создан', value: 'created' },
        { text: 'Активен', value: 'active' },
        { text: 'Остановлен', value: 'stopped' },
        { text: 'Сбой', value: 'failed' },
      ],
      // Dialogs
      projectToDelete: null,
      // Пока запрос идёт, кнопки в модалке заблокированы: повторное нажатие
      // отправляло второй запрос и возвращало ошибку на уже удалённый проект
      isDeleting: false,
      showDeleteDialog: false,
      projectToClear: null,
      showClearFindingsDialog: false,
      clearingFindings: false,
    };
  },
  computed: {
    noProjectsYet() {
      const f = this.filters
      return this.loaded && this.total === 0 && !f.search && !(f.selectedTags && f.selectedTags.length) && !f.status
    },
    ...mapState({
      projectCreated: state => state.projectCreated
    }),
    totalPages() {
      return Math.ceil(this.total / this.limit) || 1;
    },
  },
  watch: {
    projectCreated(newVal) {
      if (newVal) {
        this.$emit('show-message', 'Проект успешно сохранён!', false);
        this.$store.commit('setProjectCreated', false);
      }
    }
  },
  mounted() {
    if (this.projectCreated) {
      this.$emit('show-message', 'Проект успешно сохранён!', false);
      this.$store.commit('setProjectCreated', false);
    }
    this.fetchTags();
    this.fetchData();
  },
  methods: {
    onLimitChange() {
      try { document.cookie = `projects_per_page=${this.limit};path=/;max-age=31536000`; } catch (e) { /* без cookie */ }
      this.onPageChange(1);
    },
    firstCheckCut(item) {
      const fc = item.first_check
      return !!fc && fc.available > fc.taken
    },
    firstCheckText(fc) {
      const days = fc.days ? ` за ${fc.days} дн.` : ''
      return `Первая проверка взяла ${this.formatNumber(fc.taken)} самых свежих сообщений из ${this.formatNumber(fc.available)} совпадений${days}. ` +
        'Более старые не добавятся. Чтобы увидеть всё, уточните запрос и пересканируйте проект.'
    },
    fetchData() {
      this.isLoading = true;
      const offset = (this.currentPage - 1) * this.limit;
      const sort = this.sortBy[0] || { key: 'created_at', order: 'desc' };

      const params = {
        offset: offset,
        limit: this.limit,
        order: sort.key,
        direction: sort.order,
      };

      // Add filter params
      if (this.filters.search) {
        params.search = this.filters.search;
      }
      if (this.filters.selectedTags && this.filters.selectedTags.length > 0) {
        params.tag_ids = this.filters.selectedTags.join(',');
      }
      if (this.filters.status) {
        params.status_filter = this.filters.status;
      }

      apiClient.get('/project', { params })
        .then(response => {
          this.projects = response.data.projects || response.data.items || [];
          this.total = response.data.total;
          this.loaded = true;
        })
        .catch(error => {
          console.error("Error fetching projects:", error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    fetchTags() {
      apiClient.get('/ref/project-tags')
        .then(response => {
          this.availableTags = response.data.items || response.data || [];
        })
        .catch(error => {
          console.error("Error fetching tags:", error);
        });
    },
    onPageChange(page) {
      this.currentPage = page || 1;
      this.fetchData();
    },
    updateSort(sortByArr) {
      this.sortBy = sortByArr;
      this.currentPage = 1;
      this.fetchData();
    },
    onFilterChange() {
      this.currentPage = 1;
      this.fetchData();
    },
    resetFilters() {
      this.filters.search = '';
      this.filters.selectedTags = [];
      this.filters.status = null;
      this.currentPage = 1;
      this.fetchData();
    },
    handleRowClick(event, { item }) {
      this.$router.push({ name: 'editProject', params: { projectId: item.id } })
        .catch(err => console.error(err));
    },
    createProject() {
      this.$router.push({ name: 'createProject' })
        .catch(err => console.error(err));
    },
    editProject(project) {
      this.$router.push({ name: 'editProject', params: { projectId: project.id } })
        .catch(err => console.error(err));
    },
    deleteProject(project) {
      this.projectToDelete = project;
      this.showDeleteDialog = true;
    },
    confirmDelete() {
      if (!this.projectToDelete || this.isDeleting) return;
      this.isDeleting = true;
      apiClient.delete(`/project/${this.projectToDelete.id}`)
        .then(response => {
          if (response.status === 200) {
            this.$emit('show-message', 'Проект удален.', false);
            this.fetchData();
          } else {
            this.$emit('show-message', 'Ошибка удаления проекта.', true);
          }
        })
        .catch(error => {
          console.error("Error deleting project:", error);
          this.$emit('show-message', 'Ошибка удаления проекта.', true);
        })
        .finally(() => {
          this.isDeleting = false;
          this.projectToDelete = null;
          this.showDeleteDialog = false;
        });
    },
    startProject(project) {
      apiClient.put(`/project/${project.id}/start`)
        .then(response => {
          if (response.status === 200) {
            this.$emit('show-message', 'Проект запущен', false);
            const p = this.projects.find(p => p.id === project.id);
            if (p) p.status = 'active';
          } else {
            this.$emit('show-message', 'Ошибка запуска проекта', true);
          }
        })
        .catch(error => {
          console.error("Error starting project:", error);
          // the refusal of core (the plan limit) comes in detail
          const detail = error.response?.data?.detail;
          this.$emit('show-message', typeof detail === 'string' ? detail : 'Ошибка запуска проекта', true);
        });
    },
    stopProject(project) {
      apiClient.put(`/project/${project.id}/stop`)
        .then(response => {
          if (response.status === 200) {
            this.$emit('show-message', 'Проект остановлен', false);
            const p = this.projects.find(p => p.id === project.id);
            if (p) p.status = 'stopped';
          } else {
            this.$emit('show-message', 'Ошибка остановки проекта', true);
          }
        })
        .catch(error => {
          console.error("Error stopping project:", error);
          this.$emit('show-message', 'Ошибка остановки проекта', true);
        });
    },
    clearFindings(project) {
      this.projectToClear = project;
      this.showClearFindingsDialog = true;
    },
    confirmClearFindings() {
      if (!this.projectToClear) return;
      this.clearingFindings = true;

      apiClient.delete(`/project/${this.projectToClear.id}/findings`)
        .then(response => {
          if (response.status === 200) {
            const deletedCount = response.data.deleted_findings || 0;
            this.$emit('show-message', `Удаляются сообщения проекта: ${deletedCount}. Пересканирование запустится при следующем прогоне мониторинга.`, false);
            this.fetchData();
          } else {
            this.$emit('show-message', 'Ошибка очистки данных', true);
          }
        })
        .catch(error => {
          console.error("Error clearing findings:", error);
          this.$emit('show-message', 'Ошибка очистки данных', true);
        })
        .finally(() => {
          this.projectToClear = null;
          this.showClearFindingsDialog = false;
          this.clearingFindings = false;
        });
    },
    isInProgress(status) {
      return status === 'active';
    },
    getStatusColor(status) {
      const colors = {
        created: 'warning',
        active: 'success',
        stopped: 'info',
        failed: 'error',
      };
      return colors[status] || 'grey';
    },
    getStatusLabel(status) {
      const labels = {
        created: 'создан',
        active: 'активен',
        stopped: 'остановлен',
        failed: 'сбой',
      };
      return labels[status] || status;
    },
    formatNumber(num) {
      if (num === null || num === undefined) return '0';
      return num.toLocaleString('ru-RU');
    },
  },
};
</script>

<style scoped>
.table-column-transition {
  transition: all 0.3s ease;
}
</style>
